import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import {
    Plus, Search, Pencil, Ban, RotateCcw, FlaskConical, Layers, Ruler,
    AlertTriangle, Send, Clock, Beaker, Library,
} from 'lucide-react';
import PickFromMasterModal from '../../components/catalog/PickFromMasterModal';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
import AddLabTest, { TUBE_SWATCH } from '../../components/modals/AddLabTest';
import { getSections, getTests, getPartners, updateTest, deactivateTest } from '../../redux/labSlice';
import usePermissions from '../../hooks/usePermissions';
import useTenantFeatures from '../../hooks/useTenantFeatures';

const MySwal = withReactContent(Swal);

// THE TEST MENU — what this laboratory offers, what it costs, what it is drawn
// into, and what its numbers mean.
//
// It is the master screen that decides how a value the lab has not yet measured
// will be interpreted, flagged, priced and reported. Nothing here is cosmetic:
//
//  · THE CAP COLOUR IS SHOWN AS A COLOUR. At the phlebotomy trolley nobody reads
//    'K2-EDTA 2ml'; they reach for the lavender cap. The wrong tube does not
//    produce a wrong answer, it produces NO answer — the sample is rejected and
//    the patient, often a child, is bled a second time. A column of grey text
//    saying "Lavender" is read at the desk; a dot is read at the trolley.
//
//  · THE CONFIGURATION GAPS ARE ON THE LIST, not discovered on a report. Every
//    row carries `needsReferenceRange` and `needsComponents` for exactly this. A
//    numeric test with no band reports every result unflagged and nothing on the
//    bench announces it; a panel with no components is worse, because the order
//    is refused in front of the patient.
//
//  · WITHDRAWING IS A DELETE BUTTON THAT IS NOT A DELETE. Results, orders and
//    released reports from previous years all quote testId. The server refuses
//    to withdraw a test with work still on the bench (TEST_HAS_OPEN_RESULTS) or
//    one that sits inside a live profile (TEST_IN_PANEL), and those refusals are
//    shown verbatim — each names the exact thing to clear first, which a house
//    string like "could not withdraw" would throw away.

// ---------------------------------------------------------------------------

const money = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0).toFixed(2);

/**
 * Turnaround, said the way a person says it. 36 hours is "1 d 12 h"; 4 is "4 h".
 * A bare hour count past a day is arithmetic the reader has to do, and this
 * figure is the promise made to the patient — it is worth being legible.
 */
const formatTat = (hours) => {
    const h = parseInt(hours, 10);
    if (!Number.isFinite(h) || h <= 0) return '—';
    if (h < 24) return `${h} h`;
    const days = Math.floor(h / 24);
    const rest = h % 24;
    return rest ? `${days} d ${rest} h` : `${days} d`;
};

// One tone per specimen, so the eye sorts a page of tests by what has to be
// collected before it reads a single name. Deliberately muted — the cap colour
// beside it is the one thing on this row that must carry real colour meaning.
const SAMPLE_TONE = {
    blood: 'bg-rose-50 text-rose-700 border-rose-200',
    serum: 'bg-amber-50 text-amber-800 border-amber-200',
    plasma: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    urine: 'bg-lime-50 text-lime-800 border-lime-200',
    stool: 'bg-orange-50 text-orange-800 border-orange-200',
    swab: 'bg-sky-50 text-sky-800 border-sky-200',
    tissue: 'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200',
    fluid: 'bg-cyan-50 text-cyan-800 border-cyan-200',
    other: 'bg-slate-100 text-slate-600 border-slate-200',
};

const SAMPLE_TYPES = ['blood', 'serum', 'plasma', 'urine', 'stool', 'swab', 'tissue', 'fluid', 'other'];

export default function LabTestMaster() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { can } = usePermissions();
    const { has } = useTenantFeatures();
    const {
        tests, testsTotal, testsLoading, sections, partners, mutating,
    } = useSelector((s) => s.lab);

    const canManage = can('lab.master.manage');
    // Referring work out is sold separately, and the partner endpoints are gated
    // on it server-side. Asking first turns a screenful of 403s into a referral
    // field that simply is not there.
    const outsourcingEnabled = has('lab_outsourcing');

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [activeTab, setActiveTab] = useState('active');
    const [sectionFilter, setSectionFilter] = useState('');
    const [sampleFilter, setSampleFilter] = useState('');
    const [kindFilter, setKindFilter] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [showPickModal, setShowPickModal] = useState(false);

    const searchTimeoutRef = useRef(null);

    const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setCurrentPage(1);
        }, 500);
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [searchQuery]);

    // The benches, for the filter and for the modal's picker.
    //
    // CLOSED SECTIONS ARE FETCHED, not filtered out. The server refuses to close
    // a bench that active tests still point at, so a test filed at a closed one
    // should be impossible — but if it ever happens the row has to show the real
    // name rather than "no bench", because "no bench" sends somebody looking for
    // a filing mistake that is already recorded. The modal filters them out of
    // the picker instead, which is where offering one would actually do harm.
    useEffect(() => { dispatch(getSections()); }, [dispatch, refreshKey]);

    useEffect(() => {
        // A 403 here would mean the cached feature map is ahead of what this
        // hospital actually bought. Not asking at all is the quieter answer, and
        // nothing else on the screen depends on the list.
        if (outsourcingEnabled) dispatch(getPartners({ status: 'active' }));
    }, [dispatch, outsourcingEnabled, refreshKey]);

    useEffect(() => {
        dispatch(getTests({
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearch,
            sectionId: sectionFilter,
            sampleType: sampleFilter,
            // '' is dropped from the query string by the slice, which is what
            // makes "tests and panels" mean no filter at all rather than
            // isPanel=false.
            isPanel: kindFilter === 'panel' ? 'true' : kindFilter === 'analyte' ? 'false' : '',
            isActive: activeTab === 'active' ? 'true' : 'false',
        }));
    }, [dispatch, currentPage, itemsPerPage, debouncedSearch, activeTab, sectionFilter, sampleFilter, kindFilter, refreshKey]);

    const sectionById = useMemo(
        () => Object.fromEntries((sections || []).map((s) => [s.sectionId, s])),
        [sections]
    );
    const partnerById = useMemo(
        () => Object.fromEntries((partners || []).map((p) => [p.partnerId, p])),
        [partners]
    );

    // Counted on the page in front of the reader, not across the whole menu —
    // claiming a total the screen cannot show would send somebody hunting through
    // pages for rows that are not flagged anywhere.
    const gapCount = useMemo(
        () => (tests || []).filter((x) => x.needsReferenceRange || x.needsComponents).length,
        [tests]
    );

    const openCreate = () => { setEditing(null); setIsModalOpen(true); };
    const openEdit = (test) => { setEditing(test); setIsModalOpen(true); };

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    /**
     * Withdraw a test from the menu. Deactivated, never deleted.
     *
     * The server's refusals are the useful part and are shown verbatim:
     * TEST_HAS_OPEN_RESULTS means samples are on the bench right now,
     * TEST_IN_PANEL means the analyte would keep being measured inside profiles
     * that still list it. Each names the exact thing to clear first.
     */
    const handleWithdraw = async (test) => {
        const result = await MySwal.fire({
            title: t('labtestpage.withdraw_title', 'Withdraw this test?'),
            text: t('labtestpage.withdraw_text', 'It disappears from the order screen. Nothing is deleted — results, orders and released reports keep pointing at it.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('labtestpage.withdraw', 'Withdraw'),
            cancelButtonText: t('labtestpage.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;

        try {
            const res = await dispatch(deactivateTest(test.testId)).unwrap();
            // Order lines already raised do not block the withdrawal but they are
            // still work somebody has to finish, so the server puts the count in
            // its message rather than dropping it.
            toast.success(res?.message || t('labtestpage.withdrawn', 'Test withdrawn'));
            refetch();
        } catch (err) {
            toast.warn(typeof err?.error === 'string' ? err.error : t('labtestpage.withdraw_error', 'Failed to withdraw the test'));
        }
    };

    const handleReactivate = async (test) => {
        try {
            await dispatch(updateTest({ testId: test.testId, data: { isActive: true } })).unwrap();
            toast.success(t('labtestpage.reactivated', 'Test is back on the menu'));
            refetch();
        } catch (err) {
            toast.warn(typeof err?.error === 'string' ? err.error : t('labtestpage.reactivate_error', 'Failed to bring the test back'));
        }
    };

    const indexOfFirst = (currentPage - 1) * itemsPerPage;

    // --- row-level renderers -------------------------------------------------

    /** The cap the phlebotomist reaches for. A stored colour this screen does not
     *  recognise still gets its own wording and a hollow dot — a lab that buys a
     *  tube nobody here anticipated must not be shown a blank. */
    const TubeDot = ({ colour }) => {
        const value = String(colour || '').trim();
        if (!value) return null;
        const hex = TUBE_SWATCH[value.toLowerCase()] || null;
        return (
            <span
                data-testid="tube-dot"
                title={t('labtestpage.cap_colour', 'Cap colour: {{colour}}', { colour: value })}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-600"
            >
                <span
                    className={`w-3.5 h-3.5 rounded-full flex-shrink-0 border ${hex ? 'border-black/20' : 'border-dashed border-slate-400'}`}
                    style={hex ? { backgroundColor: hex } : undefined}
                />
                {value}
            </span>
        );
    };

    const SetupCell = ({ test }) => {
        if (test.isPanel) {
            return test.needsComponents ? (
                <span
                    data-testid={`panel-empty-${test.testId}`}
                    title={t('labtestpage.panel_empty_hint', 'Ordering this profile fails outright until it has at least one component')}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 text-[10px] font-bold"
                >
                    <AlertTriangle className="w-3 h-3" />
                    {t('labtestpage.no_components', 'No components')}
                </span>
            ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#4ABDBD]/10 text-[#2D8A8A] border border-[#4ABDBD]/30 text-[10px] font-bold">
                    <Layers className="w-3 h-3" />
                    {t('labtestpage.n_components', '{{n}} components', { n: test.componentCount || 0 })}
                </span>
            );
        }
        if (test.needsReferenceRange) {
            return (
                <span
                    data-testid={`needs-range-${test.testId}`}
                    title={t('labtestpage.needs_range_hint', 'Results for this analyte are reported with no reference interval and no flag')}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold"
                >
                    <AlertTriangle className="w-3 h-3" />
                    {t('labtestpage.no_range', 'No range')}
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 text-[10px] font-bold">
                <Ruler className="w-3 h-3" />
                {t('labtestpage.n_ranges', '{{n}} bands', { n: test.rangeCount || 0 })}
            </span>
        );
    };

    const Actions = ({ test, compact = false }) => (
        <div className={`flex ${compact ? '' : 'justify-center'} gap-2`}>
            <button
                data-testid={`edit-lab-test-${test.testId}`}
                onClick={() => openEdit(test)}
                disabled={!canManage}
                title={canManage ? t('labtestpage.edit', 'Edit') : t('labtestpage.no_permission', 'You do not have permission to change the test menu')}
                className={`text-[#4ABDBD] p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 shadow-md transition-all duration-300 ${canManage ? 'hover:text-white hover:from-[#4ABDBD] hover:to-[#3AA8A8] cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'}`}
            >
                <Pencil className="h-4 w-4" />
            </button>
            {test.isActive ? (
                <button
                    data-testid={`withdraw-lab-test-${test.testId}`}
                    onClick={() => handleWithdraw(test)}
                    disabled={!canManage || mutating}
                    title={t('labtestpage.withdraw', 'Withdraw')}
                    className={`text-red-500 p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 shadow-md transition-all duration-300 ${canManage && !mutating ? 'hover:text-white hover:from-red-500 hover:to-pink-500 cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'}`}
                >
                    <Ban className="h-4 w-4" />
                </button>
            ) : (
                <button
                    data-testid={`reactivate-lab-test-${test.testId}`}
                    onClick={() => handleReactivate(test)}
                    disabled={!canManage || mutating}
                    title={t('labtestpage.reactivate', 'Bring back')}
                    className={`text-emerald-600 p-2 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 shadow-md transition-all duration-300 ${canManage && !mutating ? 'hover:text-white hover:from-emerald-500 hover:to-teal-500 cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'}`}
                >
                    <RotateCcw className="h-4 w-4" />
                </button>
            )}
        </div>
    );

    const TestIdentity = ({ test }) => {
        const partner = test.partnerId ? partnerById[test.partnerId] : null;
        return (
            <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#4ABDBD]/10 flex items-center justify-center flex-shrink-0">
                    {test.isPanel
                        ? <Layers className="w-4 h-4 text-[#2D8A8A]" />
                        : <FlaskConical className="w-4 h-4 text-[#2D8A8A]" />}
                </div>
                <div>
                    <div className="font-semibold text-gray-700 flex items-center gap-2 flex-wrap">
                        {test.name || '-'}
                        {test.isPanel && (
                            <span className="px-1.5 py-0.5 rounded bg-[#D7A05C]/15 text-[#8a6127] border border-[#D7A05C]/40 text-[10px] font-bold uppercase tracking-wide">
                                {t('labtestpage.panel', 'Panel')}
                            </span>
                        )}
                    </div>
                    <div className="text-[11px] text-slate-400 font-mono">
                        {test.code}{test.shortName ? ` · ${test.shortName}` : ''}
                    </div>
                    {/* The report carries this hospital's letterhead and has to
                        disclose that the test was performed elsewhere, so who it
                        goes to belongs next to the name and not in a tooltip. */}
                    {test.isOutsourced && (
                        <div className="text-[11px] text-violet-700 flex items-center gap-1 mt-0.5">
                            <Send className="w-3 h-3" />
                            {partner?.name
                                ? t('labtestpage.sent_to', 'Sent to {{lab}}', { lab: partner.name })
                                : t('labtestpage.sent_out', 'Sent to a reference lab')}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div data-testid="lab-tests-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 data-testid="lab-tests-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('labtestpage.title', 'Test master')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('labtestpage.subtitle', 'What this laboratory offers — the tube it is drawn into, what it costs, how long it takes, and what its numbers mean.')}
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                    <button
                        data-testid="pick-from-master-button"
                        onClick={() => setShowPickModal(true)}
                        disabled={!canManage}
                        title={canManage ? undefined : t('labtestpage.no_permission', 'You do not have permission to change the test menu')}
                        className={`group flex items-center justify-center gap-2 px-4 py-2 border-2 border-[#3AA8A8] text-[#3AA8A8] rounded-xl transition-all duration-300 shadow-sm font-semibold text-sm whitespace-nowrap ${!canManage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-[#3AA8A8] hover:text-white hover:shadow-md'}`}
                    >
                        <Library className="w-5 h-5" />
                        {t('labtestpage.pick_from_master', 'Pick from master')}
                    </button>
                    <button
                        data-testid="add-lab-test-button"
                        onClick={openCreate}
                        disabled={testsLoading || !canManage}
                        title={canManage ? undefined : t('labtestpage.no_permission', 'You do not have permission to change the test menu')}
                        className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg transition-all duration-300 font-semibold text-sm whitespace-nowrap ${testsLoading || !canManage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-xl hover:scale-105'}`}
                    >
                        <Plus className="w-5 h-5" />
                        {t('labtestpage.add_test', 'Add test')}
                    </button>
                </div>
            </div>

            <PickFromMasterModal
                kind="lab_test"
                open={showPickModal}
                onClose={() => setShowPickModal(false)}
                onPicked={() => setRefreshKey((k) => k + 1)}
            />

            <div className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center flex-wrap">
                <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
                    {['active', 'inactive'].map((tab) => (
                        <button
                            key={tab}
                            data-testid={`lab-tests-tab-${tab}`}
                            onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === tab ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                        >
                            {activeTab === tab && (
                                <motion.div
                                    layoutId="activeLabTestStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">
                                {tab === 'active'
                                    ? t('labtestpage.on_menu', 'On the menu')
                                    : t('labtestpage.withdrawn_tab', 'Withdrawn')}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="relative w-full sm:w-80">
                    <input
                        data-testid="lab-tests-search-input"
                        type="text"
                        placeholder={t('labtestpage.search_placeholder', 'Search name / code / LOINC…')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 60))}
                        maxLength={60}
                        className="w-full p-2 pl-10 pr-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>

                <select
                    data-testid="lab-tests-section-filter"
                    value={sectionFilter}
                    onChange={(e) => { setSectionFilter(e.target.value); setCurrentPage(1); }}
                    className="tt-select w-full sm:w-48 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('labtestpage.all_sections', 'All benches')}</option>
                    {(sections || []).map((s) => (
                        <option key={s.sectionId} value={s.sectionId}>
                            {s.name}{s.status !== 'active' ? ` · ${t('labtestpage.closed', 'closed')}` : ''}
                        </option>
                    ))}
                </select>

                <select
                    data-testid="lab-tests-sample-filter"
                    value={sampleFilter}
                    onChange={(e) => { setSampleFilter(e.target.value); setCurrentPage(1); }}
                    className="tt-select w-full sm:w-40 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] capitalize"
                >
                    <option value="">{t('labtestpage.all_samples', 'All specimens')}</option>
                    {SAMPLE_TYPES.map((s) => (
                        <option key={s} value={s}>{t(`labtestpage.sample_${s}`, s)}</option>
                    ))}
                </select>

                <select
                    data-testid="lab-tests-kind-filter"
                    value={kindFilter}
                    onChange={(e) => { setKindFilter(e.target.value); setCurrentPage(1); }}
                    className="tt-select w-full sm:w-44 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('labtestpage.all_kinds', 'Tests and panels')}</option>
                    <option value="analyte">{t('labtestpage.analytes_only', 'Single tests only')}</option>
                    <option value="panel">{t('labtestpage.panels_only', 'Panels only')}</option>
                </select>
            </div>

            {/* The gaps that are invisible everywhere else. A missing band does not
                break anything the software can detect — it just means every result
                for that analyte goes out with no reference interval and no flag. */}
            {!testsLoading && gapCount > 0 && activeTab === 'active' && (
                <div data-testid="lab-tests-setup-gaps" className="mb-4 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {t('labtestpage.setup_gaps', '{{n}} test(s) on this page are not finished being set up. A numeric test with no reference band reports every result unflagged; a panel with no components cannot be ordered at all.', { n: gapCount })}
                </div>
            )}

            {testsLoading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('labtestpage.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    <div className="hidden lg:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="lab-tests-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labtestpage.serial_no', 'S.No')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labtestpage.test', 'Test')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labtestpage.section', 'Bench')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labtestpage.specimen', 'Specimen / tube')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labtestpage.price', 'Price')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labtestpage.tat', 'TAT')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labtestpage.setup', 'Setup')}</th>
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('labtestpage.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="lab-tests-table-body">
                                {(tests || []).length > 0 ? tests.map((test, index) => {
                                    const section = sectionById[test.sectionId];
                                    return (
                                        <tr
                                            key={test.testId}
                                            data-testid={`lab-test-row-${test.testId}`}
                                            className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300"
                                        >
                                            <td className="p-3 text-xs text-gray-600">{indexOfFirst + index + 1}</td>
                                            <td className="p-3 text-xs"><TestIdentity test={test} /></td>
                                            <td className="p-3 text-xs">
                                                {test.sectionId ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#4ABDBD]/10 text-[#2D8A8A] text-[11px] font-semibold">
                                                        {test.sectionName || section?.name || '—'}
                                                    </span>
                                                ) : (
                                                    // A test with no bench lands on no worklist, so nobody
                                                    // is ever told its sample is waiting.
                                                    <span
                                                        data-testid={`no-section-${test.testId}`}
                                                        title={t('labtestpage.no_section_hint', 'A test with no bench appears on no worklist')}
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-dashed border-amber-300 text-[11px] font-semibold"
                                                    >
                                                        <AlertTriangle className="w-3 h-3" />
                                                        {t('labtestpage.unassigned', 'No bench')}
                                                    </span>
                                                )}
                                                {section && section.status !== 'active' && (
                                                    <div className="text-[10px] text-amber-600 mt-1">{t('labtestpage.bench_closed', 'bench is closed')}</div>
                                                )}
                                            </td>
                                            <td className="p-3 text-xs text-gray-600">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-semibold capitalize ${SAMPLE_TONE[test.sampleType] || SAMPLE_TONE.other}`}>
                                                    <Beaker className="w-3 h-3" />
                                                    {t(`labtestpage.sample_${test.sampleType}`, test.sampleType || 'other')}
                                                </span>
                                                <div className="mt-1 flex flex-col gap-0.5">
                                                    <TubeDot colour={test.containerColour} />
                                                    {test.containerType && (
                                                        <span className="text-[11px] text-slate-400">{test.containerType}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-3 text-xs text-gray-700 font-semibold whitespace-nowrap">
                                                ₹{money(test.price)}
                                                {test.isOutsourced && Number(test.partnerRate) > 0 && (
                                                    // The cost side of the same line, shown only where it
                                                    // exists — a referral that costs more than the patient
                                                    // pays is a margin nobody reads a tariff sheet
                                                    // carefully enough to catch.
                                                    <div className="text-[10px] text-slate-400 font-normal">
                                                        {t('labtestpage.costs', 'costs ₹{{rate}}', { rate: money(test.partnerRate) })}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-3 text-xs text-gray-600 whitespace-nowrap">
                                                <span className="inline-flex items-center gap-1">
                                                    <Clock className="w-3 h-3 text-[#4ABDBD]" />
                                                    {formatTat(test.tatHours)}
                                                </span>
                                            </td>
                                            <td className="p-3 text-xs">
                                                <SetupCell test={test} />
                                                {test.unit && (
                                                    <div className="text-[10px] text-slate-400 mt-1">{test.unit}</div>
                                                )}
                                            </td>
                                            <td className="p-3 text-xs"><Actions test={test} /></td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan={8} className="p-6 text-center text-gray-500">
                                            {activeTab === 'active'
                                                ? t('labtestpage.no_tests', 'Nothing on the menu yet. Add a test to give doctors something to order and the bench something to work.')
                                                : t('labtestpage.no_withdrawn', 'Nothing has been withdrawn.')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Cards below lg — eight columns do not survive a narrow screen,
                        and the lab is one of the places a tablet is used. */}
                    <div className="block lg:hidden space-y-4">
                        {(tests || []).length > 0 ? tests.map((test) => (
                            <div key={test.testId} data-testid={`lab-test-card-${test.testId}`} className="p-4 bg-gray-50 rounded-xl shadow-md">
                                <div className="flex justify-between items-start gap-2 mb-2">
                                    <TestIdentity test={test} />
                                    <Actions test={test} compact />
                                </div>
                                <div className="flex flex-wrap items-center gap-2 mt-3">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-semibold capitalize ${SAMPLE_TONE[test.sampleType] || SAMPLE_TONE.other}`}>
                                        <Beaker className="w-3 h-3" />
                                        {t(`labtestpage.sample_${test.sampleType}`, test.sampleType || 'other')}
                                    </span>
                                    <TubeDot colour={test.containerColour} />
                                    <SetupCell test={test} />
                                </div>
                                <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-gray-600">
                                    <span className="font-semibold text-gray-700">₹{money(test.price)}</span>
                                    <span className="inline-flex items-center gap-1">
                                        <Clock className="w-3 h-3 text-[#4ABDBD]" />
                                        {formatTat(test.tatHours)}
                                    </span>
                                    {test.sectionName && (
                                        <span className="px-2 py-0.5 rounded bg-white border border-gray-200 text-[11px]">{test.sectionName}</span>
                                    )}
                                    {test.containerType && (
                                        <span className="text-[11px] text-slate-400">{test.containerType}</span>
                                    )}
                                </div>
                            </div>
                        )) : (
                            <div className="p-6 text-center text-gray-500 text-sm">
                                {activeTab === 'active'
                                    ? t('labtestpage.no_tests', 'Nothing on the menu yet. Add a test to give doctors something to order and the bench something to work.')
                                    : t('labtestpage.no_withdrawn', 'Nothing has been withdrawn.')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {!testsLoading && testsTotal > 0 && (
                <Pagination
                    totalItems={testsTotal}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                />
            )}

            <AddLabTest
                isOpen={isModalOpen}
                editing={editing}
                sections={sections}
                partners={partners}
                outsourcingEnabled={outsourcingEnabled}
                onClose={() => { setIsModalOpen(false); setEditing(null); }}
                onSaved={refetch}
            />
        </div>
    );
}
